/**
 * Path Builder Utilities
 *
 * Orthogonal polylines with circular-arc fillets at 90° bends (SVG `A`, not `Q`).
 * Snaps ELK/router float noise to strict horizontal/vertical segments.
 *
 * UNIVERSAL RULE: corner radius cannot exceed half the shorter adjacent leg.
 */

import { CORNER_RADIUS, ROUTING_OBSTACLE_INFLATION } from './constants';

const DEBUG_PATHS = false;
const AXIS_SNAP_PX = 0.75;
const COLLINEAR_EPS = 0.08;

/**
 * @param {number} dist1
 * @param {number} dist2
 * @param {number} requestedRadius
 */
function maxSafeRadius(dist1, dist2, requestedRadius = CORNER_RADIUS) {
  const maxAllowed = Math.min(Math.abs(dist1), Math.abs(dist2)) / 2;
  return Math.max(0, Math.min(requestedRadius, maxAllowed));
}

/**
 * Snap nearly-axis-aligned segments and fix accidental diagonals to Manhattan steps.
 */
function snapOrthogonalPolyline(points, snapPx = AXIS_SNAP_PX) {
  if (!points.length) return points;
  const out = [{ x: points[0].x, y: points[0].y }];
  for (let i = 1; i < points.length; i += 1) {
    let p = { x: points[i].x, y: points[i].y };
    const pr = out[out.length - 1];
    const dx = p.x - pr.x;
    const dy = p.y - pr.y;
    if (Math.abs(dx) < 0.05 && Math.abs(dy) < 0.05) continue;

    if (Math.abs(dx) < snapPx) p = { x: pr.x, y: p.y };
    else if (Math.abs(dy) < snapPx) p = { x: p.x, y: pr.y };
    else p = Math.abs(dx) >= Math.abs(dy) ? { x: p.x, y: pr.y } : { x: pr.x, y: p.y };

    const dx2 = p.x - pr.x;
    const dy2 = p.y - pr.y;
    if (Math.abs(dx2) < 0.05 && Math.abs(dy2) < 0.05) continue;
    out.push(p);
  }
  return out;
}

/** Remove 180° vertices (three collinear points). */
function removeCollinearVertices(pts) {
  if (pts.length < 3) return pts;
  const out = [pts[0]];
  for (let i = 1; i < pts.length - 1; i += 1) {
    const a = out[out.length - 1];
    const b = pts[i];
    const c = pts[i + 1];
    const cross = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
    if (Math.abs(cross) < COLLINEAR_EPS) continue;
    out.push(b);
  }
  out.push(pts[pts.length - 1]);
  return out;
}

/**
 * Append line to arc start + 90° elliptical arc (rx=ry=r) to arc end.
 * Incoming: prev → curr, outgoing: curr → next. User space y increases downward.
 *
 * @returns {string} path suffix (starts with L or A as needed)
 */
function orthogonalCornerSuffix(prev, curr, next, requestedR) {
  const v1x = curr.x - prev.x;
  const v1y = curr.y - prev.y;
  const v2x = next.x - curr.x;
  const v2y = next.y - curr.y;
  const len1 = Math.hypot(v1x, v1y);
  const len2 = Math.hypot(v2x, v2y);
  if (len1 < 1e-6 || len2 < 1e-6) return ` L ${curr.x} ${curr.y}`;

  const cross = v1x * v2y - v1y * v2x;
  if (Math.abs(cross) < 0.02 * Math.max(len1, len2)) {
    return ` L ${curr.x} ${curr.y}`;
  }

  const r = maxSafeRadius(len1, len2, requestedR);
  if (r < 2) return ` L ${curr.x} ${curr.y}`;

  const n1x = v1x / len1;
  const n1y = v1y / len1;
  const n2x = v2x / len2;
  const n2y = v2y / len2;

  const sx = curr.x - n1x * r;
  const sy = curr.y - n1y * r;
  const ex = curr.x + n2x * r;
  const ey = curr.y + n2y * r;

  // SVG positive-angle direction is clockwise in y-down coordinates.
  const sweep = cross > 0 ? 1 : 0;

  if (DEBUG_PATHS) {
    console.log('[orthogonalCornerSuffix]', { prev, curr, next, r, sx, sy, ex, ey, sweep });
  }

  return ` L ${sx} ${sy} A ${r} ${r} 0 0 ${sweep} ${ex} ${ey}`;
}

/**
 * Build a path with circular fillets at orthogonal corners.
 *
 * @param {Array<{x:number,y:number}>} points
 * @param {number} [requestedRadius]
 */
export function buildPathWithCorners(points, requestedRadius = CORNER_RADIUS) {
  if (points.length < 2) return '';

  const hasInvalidPoint = points.some((p) => !p || p.x == null || p.y == null);
  if (hasInvalidPoint) return '';

  if (points.length === 2) {
    return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
  }

  let adjusted = snapOrthogonalPolyline(points);
  adjusted = removeCollinearVertices(adjusted);

  if (adjusted.length < 2) return '';
  if (adjusted.length === 2) {
    return `M ${adjusted[0].x} ${adjusted[0].y} L ${adjusted[1].x} ${adjusted[1].y}`;
  }

  let path = `M ${adjusted[0].x} ${adjusted[0].y}`;
  for (let i = 1; i < adjusted.length - 1; i += 1) {
    const prev = adjusted[i - 1];
    const curr = adjusted[i];
    const next = adjusted[i + 1];
    path += orthogonalCornerSuffix(prev, curr, next, requestedRadius);
  }

  const last = adjusted[adjusted.length - 1];
  path += ` L ${last.x} ${last.y}`;

  return path;
}

/**
 * Build simple down-across-down path with rounded corners
 * NEVER creates diagonal lines - always orthogonal
 */
export function buildSimplePath(startX, startY, endX, endY, midY, r) {
  const pts = [
    { x: startX, y: startY },
    { x: startX, y: midY },
    { x: endX, y: midY },
    { x: endX, y: endY },
  ];
  const filtered = pts.filter((p, i) => {
    if (i === 0) return true;
    const prev = pts[i - 1];
    return Math.abs(p.x - prev.x) > 0.5 || Math.abs(p.y - prev.y) > 0.5;
  });
  return buildPathWithCorners(filtered, r);
}

/**
 * Build orthogonal path with optional intermediate X positions
 */
export function buildOrthogonalPath(startX, startY, endX, endY, midY, viaStartX, viaEndX, r) {
  if (viaStartX == null) viaStartX = startX;
  if (viaEndX == null) viaEndX = endX;

  if (Math.abs(viaStartX - startX) < 1 && Math.abs(viaEndX - endX) < 1) {
    return buildSimplePath(startX, startY, endX, endY, midY, r);
  }

  const pts = [];
  pts.push({ x: startX, y: startY });

  if (Math.abs(viaStartX - startX) > 1) {
    const shiftY = startY + ROUTING_OBSTACLE_INFLATION;
    pts.push({ x: startX, y: shiftY });
    pts.push({ x: viaStartX, y: shiftY });
  }

  pts.push({ x: viaStartX, y: midY });
  pts.push({ x: viaEndX, y: midY });

  if (Math.abs(viaEndX - endX) > 1) {
    const shiftY = endY - ROUTING_OBSTACLE_INFLATION;
    pts.push({ x: viaEndX, y: shiftY });
    pts.push({ x: endX, y: shiftY });
  }

  pts.push({ x: endX, y: endY });

  const filtered = pts.filter((p, i) => {
    if (i === 0) return true;
    const prev = pts[i - 1];
    return Math.abs(p.x - prev.x) > 0.5 || Math.abs(p.y - prev.y) > 0.5;
  });

  if (DEBUG_PATHS) {
    console.log('[buildOrthogonalPath] Points:', filtered);
  }

  return buildPathWithCorners(filtered, r);
}
